import { UserRole, UserStatus } from '@fsp/types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  ArrowUpward,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import PaperSection from '../../components/PaperSection';
import UserAvatar from '../../components/UserAvatar';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services/apiClient';
import { updateUserProfile } from '../../services/user';
import { formatDate } from '@fsp/shared';
import DangerZone from './components/DangerZone';
import { useNavigate } from 'react-router-dom';

export const ProfileRoles = [
  UserRole.fsp,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
  UserRole.AUDITOR,
  UserRole.APPROVER,
];

// Form validation schema
const profileSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .trim()
    .min(1, 'First name cannot be empty')
    .max(50, 'First name must be less than 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .trim()
    .min(1, 'Last name cannot be empty')
    .max(50, 'Last name must be less than 50 characters'),
});

type ProfileFormData = {
  firstName: string;
  lastName: string;
};

const Profile: React.FC = () => {
  const { user, loading, refreshUserOrganisationState } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    watch,
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    },
    mode: 'onChange',
  });

  // Watch form values for avatar display
  const watchedValues = watch();

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      // Only send changed fields
      const updatePayload: Partial<ProfileFormData> = {};

      if (data.firstName !== user?.firstName) {
        updatePayload.firstName = data.firstName.trim();
      }
      if (data.lastName !== user?.lastName) {
        updatePayload.lastName = data.lastName.trim();
      }

      // If no changes, don't make API call
      if (Object.keys(updatePayload).length === 0) {
        enqueueSnackbar('No changes to save', { variant: 'info' });
        return;
      }

      const response = await apiClient.put(`/user/profile`, {
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      await response.json();

      // Update user context with new data
      await updateUserProfile({
        ...user!,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
      });

      await refreshUserOrganisationState();

      // Reset form with new values to clear dirty state
      reset({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
      });

      enqueueSnackbar('Profile updated successfully!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update profile. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Profile update error:', error);
    }
  };

  const handleCancel = () => {
    reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
  };

  const handleClose = () => {
    navigate('/settings');
  };

  const headerActions = (
    <IconButton onClick={handleClose}>
      <ArrowUpward />
    </IconButton>
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar('User ID copied to clipboard', { variant: 'info' });
  };

  const getEmailVerificationStatus = () => {
    if (user?.emailVerified) {
      return {
        icon: <VerifiedIcon color="success" fontSize="small" />,
        text: 'Verified',
        color: 'success.main',
        helperText: 'Your email address has been verified',
      };
    } else {
      return {
        icon: <WarningIcon color="warning" fontSize="small" />,
        text: 'Unverified',
        color: 'warning.main',
        helperText: 'Please check your email for verification instructions',
      };
    }
  };

  if (loading) {
    return (
      <ContentWrapper breadcrumb>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </ContentWrapper>
    );
  }

  const emailStatus = getEmailVerificationStatus();

  return (
    <ContentWrapper breadcrumb>
      <PaperSection
        title="Profile Settings"
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Last login: {formatDate(user?.lastLoginAt)}
            </Typography>
            {headerActions}
          </Stack>
        }
      >
        {/* Header */}
        <Box mb={4}>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                User ID:
              </Typography>
              <Chip
                label={user?.id}
                size="small"
                variant="outlined"
                onDelete={undefined}
                sx={{ backgroundColor: '#fff' }}
                deleteIcon={
                  <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
                    <IconButton size="small" onClick={() => copyToClipboard(user?.id || '')}>
                      {copied ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <CopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                }
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Status:
              </Typography>
              <Chip
                label={user?.status || UserStatus.ACTIVE}
                size="small"
                color={user?.status === UserStatus.ACTIVE ? 'success' : 'default'}
              />
            </Stack>
          </Stack>
        </Box>
        <Divider sx={{ mb: 4 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={4} maxWidth={700}>
            {/* Personal Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Personal Information
              </Typography>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="top" spacing={3}>
                  <UserAvatar
                    avatarUrl={user?.avatarUrl}
                    initials={watchedValues.firstName?.[0] + watchedValues.lastName?.[0]}
                    size="large"
                    color="primary.main"
                  />
                  <Stack spacing={2} flex={1}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="First Name"
                          variant="outlined"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                        />
                      )}
                    />

                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Last Name"
                          variant="outlined"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                        />
                      )}
                    />

                    <TextField
                      fullWidth
                      label="Email"
                      value={user?.email || ''}
                      variant="filled"
                      disabled
                      helperText={emailStatus.helperText}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {emailStatus.icon}
                              <Typography
                                variant="caption"
                                sx={{ color: emailStatus.color, fontWeight: 500 }}
                              >
                                {emailStatus.text}
                              </Typography>
                            </Stack>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Stack>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        To change your password and login details, visit{' '}
                        <Link href="/settings/security" underline="none">
                          Security Settings.
                        </Link>
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {isDirty && (
                <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </PaperSection>
      <DangerZone />
    </ContentWrapper>
  );
};

export default Profile;
